import { Box, Button, IconButton, Stack, Typography } from "@mui/material"
import Iconify from "minimals-template/components/Iconify"
import { AuthoringEditor } from "authoring/components/authoring-editor"
import { useDarkMode } from "lib/use-dark-mode"
import { If } from "lib/@components/switch"
import { Else } from "lib/@components/else"
import { DATE_FORMATS } from "lib/@constants/date-format"
import { UserAvatar } from "minimals-template/components/user-avatar"
import { hasAmendsContent } from "../lib/amends-content"
import { useEffect, useMemo, useRef } from "react"
import * as yup from "yup"
import { sanitizeAmendsHtml } from "../lib/sanitize-amends-html"
import { MacroExpander } from "authoring/routes/macro/components/macro-expander"

const REQUIRED_AMENDS_ERROR_MESSAGE = "Author notes are required."
const HELPER_TEXT_PLACEHOLDER = "\u00A0"
const EDITOR_FOCUS_SELECTOR = '.jodit-wysiwyg[contenteditable="true"]'
const EDITOR_OPEN_DELAY_MS = 120
const AMENDS_CONTENT_SCHEMA = yup
    .string()
    .test("has-amends-content", REQUIRED_AMENDS_ERROR_MESSAGE, validateAmendsContent)

export function AmendsEditor({
    title = "Add Author Notes",
    subtitle,
    editMode = true,
    value,
    onChange,
    onCancel,
    onSave,
    onEdit,
    onDelete,
    showDelete = false,
    canEdit = true,
    authorName,
    savedAt,
    busy = false,
    maxWidth = 760,
    saveCaption = "Save",
    cancelCaption = "Cancel",
    autoFocusOnOpen = true,
    hideActions = false,
    required = true,
    dataCy = "amends-editor",
}) {
    const isDark = useDarkMode()
    const savedAtLabel = formatSavedAt(savedAt)
    const saveDisabled = !canEdit || (required && !hasAmendsContent(value)) || busy
    const editorContainerRef = useRef(null)
    const validationMessage = useMemo(getValidationMessage, [required, value])
    let subtitleColor = "text.secondary"
    if (isDark) subtitleColor = "#b4b4b4"
    useEffect(openAndFocusEditor, [editMode, autoFocusOnOpen])

    if (!editMode) {
        const safeReadonlyHtml = sanitizeAmendsHtml(value)
        return (
            <Box sx={{ backgroundColor: "action.hover", borderRadius: 2, p: 2 }} data-cy="amends-readonly-card">
                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Iconify icon="mdi:message-text-outline" width={20} />
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            Author Notes
                        </Typography>
                    </Stack>
                    <IconButton
                        aria-label="Edit author notes"
                        size="small"
                        color="primary"
                        onClick={onEdit}
                        disabled={!canEdit}
                        data-cy="amends-edit-button"
                    >
                        <Iconify icon="mdi:pencil" width={18} />
                    </IconButton>
                </Stack>
                <MacroExpander>
                    <Box
                        mt={1.5}
                        data-cy="amends-readonly-content"
                        sx={{ "& p": { m: 0 }, "& p + p": { mt: 1 } }}
                        dangerouslySetInnerHTML={{ __html: safeReadonlyHtml }} // sanitized by sanitizeAmendsHtml above
                    />
                </MacroExpander>
                <If truthy={authorName || savedAtLabel}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1.5 }}>
                        <If truthy={authorName}>
                            <UserAvatar
                                user={{ displayName: authorName }}
                                data-cy="amends-author-avatar"
                                sx={{
                                    width: 22,
                                    height: 22,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    "& .MuiTypography-root": {
                                        fontSize: 11,
                                        lineHeight: 1,
                                        textAlign: "center",
                                        transform: "translateY(1px)",
                                    },
                                }}
                            />
                        </If>
                        <Typography variant="body2" sx={{ color: "text.secondary" }} data-cy="amends-meta">
                            {[authorName, savedAtLabel].filter(Boolean).join(", ")}
                        </Typography>
                    </Stack>
                </If>
            </Box>
        )
    }

    return (
        <Box data-cy={dataCy}>
            <Typography variant="subtitle2" sx={{ color: "text.secondary" }}>
                {title}
            </Typography>
            <If truthy={subtitle}>
                <Typography variant="caption" sx={{ color: subtitleColor }}>
                    {subtitle}
                </Typography>
            </If>
            <Box mt={1} sx={{ width: 1, maxWidth }} data-cy="amends-rich-text" ref={editorContainerRef}>
                <AuthoringEditor
                    value={value}
                    onChange={onChange}
                    error={Boolean(validationMessage)}
                    helperText={validationMessage || HELPER_TEXT_PLACEHOLDER}
                />
            </Box>
            <If value={!hideActions}>
                <Stack direction="row" spacing={1} justifyContent="flex-end" mt={1.5}>
                    <If truthy={showDelete}>
                        <Button
                            onClick={onDelete}
                            variant="text"
                            color="error"
                            startIcon={<Iconify icon="mdi:delete" />}
                            data-cy="amends-delete-button"
                            disabled={busy || !canEdit}
                        >
                            Delete
                        </Button>
                        <Else>
                            <Button
                                onClick={onCancel}
                                variant="text"
                                color="inherit"
                                startIcon={<Iconify icon="mdi:close-circle-outline" />}
                                data-cy="amends-cancel-button"
                                disabled={busy}
                            >
                                {cancelCaption}
                            </Button>
                        </Else>
                    </If>

                    <Button
                        onClick={onSave}
                        variant="contained"
                        startIcon={<Iconify icon="mdi:check" />}
                        data-cy="amends-save-button"
                        disabled={saveDisabled}
                    >
                        {saveCaption}
                    </Button>
                </Stack>
            </If>
        </Box>
    )

    function openAndFocusEditor() {
        let timeoutId = null

        if (editMode && autoFocusOnOpen) {
            timeoutId = setTimeout(() => {
                const editorContainer = editorContainerRef.current
                if (!editorContainer) return

                scrollEditorIntoView(editorContainer)
                focusEditorContent(editorContainer)
                scrollEditorIntoView(editorContainer)
            }, EDITOR_OPEN_DELAY_MS)
        }

        return () => {
            if (timeoutId) clearTimeout(timeoutId)
        }
    }

    function getValidationMessage() {
        if (!required) return null

        try {
            AMENDS_CONTENT_SCHEMA.validateSync(value)
            return null
        } catch (error) {
            return error?.message ?? REQUIRED_AMENDS_ERROR_MESSAGE
        }
    }
}

function formatSavedAt(savedAt) {
    if (!savedAt) return null
    const date = new Date(savedAt)
    if (Number.isNaN(date.getTime())) return savedAt
    return date.format(DATE_FORMATS.long)
}

function validateAmendsContent(value) {
    return hasAmendsContent(value)
}

function scrollEditorIntoView(editorContainer) {
    editorContainer.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" })
}

function focusEditorContent(editorContainer) {
    const focusTarget = editorContainer.querySelector(EDITOR_FOCUS_SELECTOR)
    if (typeof focusTarget?.focus !== "function") return

    try {
        focusTarget.focus({ preventScroll: true })
    } catch (error) {
        focusTarget.focus()
    }
}
